import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import localeConfig from '../../config/locale.config';

const VendorDriversForm = ({ data, onNext, onPrevious, onSave, loading, colors, isPending, registrationStatus }) => {
    const [drivers, setDrivers] = useState([]);
    const [expandedDrivers, setExpandedDrivers] = useState(new Set([])); // Keep first driver collapsed by default

    useEffect(() => {
        if (data.drivers && data.drivers.length > 0) {
            // Filter out empty driver records
            const validDrivers = data.drivers.filter(driver => 
                driver.name && driver.name.trim() !== ''
            );
            setDrivers(validDrivers);
            // Keep first driver collapsed by default
            setExpandedDrivers(new Set([]));
        } else {
            // Start with no drivers
            setDrivers([]);
            setExpandedDrivers(new Set([]));
        }
    }, [data.drivers]);

    const toggleDriverExpansion = (index) => {
        const newExpanded = new Set(expandedDrivers);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedDrivers(newExpanded);
    };

    const handleDriverChange = (index, field, value) => {
        const updatedDrivers = [...drivers];
        updatedDrivers[index] = {
            ...updatedDrivers[index],
            [field]: value
        };
        setDrivers(updatedDrivers);
    };

    const addDriver = () => {
        const newIndex = drivers.length;
        setDrivers([...drivers, {
            // User fields (will be created in users table)
            username: '',
            name: '',
            mobile_number: '',
            // Driver-specific fields
            vehicle_number: '',
            experience_years: '',
            // Vendor driver relationship
            vendor_location_id: '',
            is_active: true
        }]);
        // Auto-expand the new driver
        setExpandedDrivers(new Set([...expandedDrivers, newIndex]));
    };

    const removeDriver = (index) => {
        const updatedDrivers = drivers.filter((_, i) => i !== index);
        setDrivers(updatedDrivers);
        
        // Update expanded drivers
        const newExpanded = new Set();
        expandedDrivers.forEach(expandedIndex => {
            if (expandedIndex < index) {
                newExpanded.add(expandedIndex);
            } else if (expandedIndex > index) {
                newExpanded.add(expandedIndex - 1);
            }
        });
        setExpandedDrivers(newExpanded);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Prevent submission if registration is pending
        if (isPending) {
            return;
        }
        
        // Validate that all drivers have required fields
        const invalidDrivers = drivers.filter(driver => 
            !driver.name || !driver.name.trim() || 
            !driver.mobile_number || !driver.mobile_number.trim() || 
            !driver.vendor_location_id || driver.vendor_location_id === ''
        );
        
        if (invalidDrivers.length > 0) {
            alert('Please fill in all required fields (Name, Mobile Number, and Assigned Location) for all drivers.');
            return;
        }
        
        // Filter out empty driver records before saving
        const validDrivers = drivers.filter(driver => 
            driver.name && driver.name.trim() !== ''
        );
        
        onNext({ drivers: validDrivers });
    };

    const handleSave = () => {
        // Prevent saving if registration is pending
        if (isPending) {
            onSave({});
            return;
        }

        // Validate that all drivers have required fields
        const invalidDrivers = drivers.filter(driver => 
            !driver.name || !driver.name.trim() || 
            !driver.mobile_number || !driver.mobile_number.trim() || 
            !driver.vendor_location_id || driver.vendor_location_id === ''
        );
        
        if (invalidDrivers.length > 0) {
            alert('Please fill in all required fields (Name, Mobile Number, and Assigned Location) for all drivers before saving.');
            return;
        }
        
        // Filter out empty driver records before saving
        const validDrivers = drivers.filter(driver => 
            driver.name && driver.name.trim() !== ''
        );
        
        onSave({ drivers: validDrivers });
    };

    const formatMobileNumber = (value) => {
        const digits = value.replace(/\D/g, '');
        return digits.slice(0, 10);
    };

    const getDriverSummary = (driver) => {
        const name = driver.name || 'No name';
        const mobile = driver.mobile_number || 'No mobile';
        const location = data.locations?.find(loc => loc.id === driver.vendor_location_id);
        const locationName = location ? `Location ${data.locations.indexOf(location) + 1}` : 'No location';
        
        return `${name} - ${mobile} - ${locationName}`;
    };

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Vendor Drivers</h2>
                <p className="text-sm text-gray-600">Add the drivers who will be working for your vendor business. (Optional)</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Driver Cards */}
                <div className="space-y-3">
                    {drivers.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">No drivers added yet.</p>
                            <p className="text-xs mt-1">Click "Add Driver" below to add driver information.</p>
                        </div>
                    )}
                    {drivers.map((driver, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Driver Header */}
                            <div 
                                className="bg-blue-50 px-4 py-2.5 cursor-pointer hover:bg-blue-100 transition-colors border-b border-blue-100"
                                onClick={() => toggleDriverExpansion(index)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        {expandedDrivers.has(index) ? (
                                            <ChevronDown className="w-4 h-4 text-gray-500" />
                                        ) : (
                                            <ChevronRight className="w-4 h-4 text-gray-500" />
                                        )}
                                        <div className="flex items-center space-x-3">
                                            <h3 className="text-base font-medium text-gray-900">
                                                Driver {index + 1}
                                            </h3>
                                            {!driver.is_active && (
                                                <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                                    Inactive
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-600 max-w-xs truncate">
                                            {getDriverSummary(driver)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeDriver(index);
                                            }}
                                            className="text-red-600 hover:text-red-800 p-1"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Driver Content */}
                            {expandedDrivers.has(index) && (
                                <div className="p-4 bg-white">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* User Information Section */}
                                        <div className="md:col-span-2">
                                            <h4 className="text-sm font-medium text-gray-900 mb-2 border-b pb-1">User Information</h4>
                                        </div>

                                        {/* Driver Name */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Driver Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={driver.name}
                                                onChange={(e) => handleDriverChange(index, 'name', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Enter driver name"
                                                required
                                            />
                                        </div>

                                        {/* Username */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Username
                                            </label>
                                            <input
                                                type="text"
                                                value={driver.username}
                                                onChange={(e) => handleDriverChange(index, 'username', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Auto-generated if left empty"
                                            />
                                        </div>

                                        {/* Mobile Number */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Mobile Number *
                                            </label>
                                            <input
                                                type="tel"
                                                value={driver.mobile_number}
                                                onChange={(e) => handleDriverChange(index, 'mobile_number', formatMobileNumber(e.target.value))}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder={localeConfig.mobile.placeholder}
                                                required
                                            />
                                        </div>

                                        {/* Vendor Location */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Assigned Location *
                                            </label>
                                            <select
                                                value={driver.vendor_location_id}
                                                onChange={(e) => handleDriverChange(index, 'vendor_location_id', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            >
                                                <option value="">Select Location</option>
                                                {data.locations && data.locations.map((location, locIndex) => (
                                                    <option key={locIndex} value={locIndex}>
                                                        Location {locIndex + 1} - {location.location_address?.address || 'No address'}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Driver Details Section */}
                                        <div className="md:col-span-2">
                                            <h4 className="text-sm font-medium text-gray-900 mb-2 border-b pb-1">Driver Details</h4>
                                        </div>



                                        {/* Vehicle Number */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Vehicle Number
                                            </label>
                                            <input
                                                type="text"
                                                value={driver.vehicle_number}
                                                onChange={(e) => handleDriverChange(index, 'vehicle_number', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Enter vehicle number"
                                            />
                                        </div>

                                        {/* Experience Years */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Experience (Years)
                                            </label>
                                            <input
                                                type="number"
                                                value={driver.experience_years}
                                                onChange={(e) => handleDriverChange(index, 'experience_years', e.target.value)}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Enter years of experience"
                                                min="0"
                                                max="50"
                                            />
                                        </div>

                                        {/* Is Active */}
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`active-${index}`}
                                                checked={driver.is_active}
                                                onChange={(e) => handleDriverChange(index, 'is_active', e.target.checked)}
                                                className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor={`active-${index}`} className="ml-2 block text-xs text-gray-700">
                                                Active Driver
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Add Driver Button */}
                <button
                    type="button"
                    onClick={addDriver}
                    className="flex items-center space-x-2 px-3 py-1.5 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Another Driver</span>
                </button>

                {/* Navigation Buttons */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onPrevious}
                        disabled={loading}
                        className="flex items-center justify-center space-x-2 px-3 py-2 sm:py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        <span>Previous</span>
                    </button>

                    <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={loading}
                            className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span>Save & Exit</span>
                        </button>

                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center justify-center space-x-2 w-full sm:w-auto px-4 py-2 sm:py-1.5 text-sm text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                backgroundColor: colors.primary
                            }}
                        >
                            <span>{loading ? 'Saving...' : 'Next'}</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default VendorDriversForm; 